import axios from 'axios';
import type { CustomerActivity, WorkCustomer } from '../api/client';

function isCustomerActivity(value: unknown): value is CustomerActivity {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  const fields = ['activity_id', 'customer_id', 'provider', 'customer_name', 'notes', 'author_name', 'created_at', 'occurred_at'];
  return fields.every(field => typeof item[field] === 'string' && String(item[field]).trim())
    && typeof item.activity_type === 'string'
    && ['visit', 'machine_replacement', 'maintenance', 'phone_call', 'note'].includes(item.activity_type)
    && Number.isFinite(Date.parse(String(item.occurred_at)))
    && Number.isFinite(Date.parse(String(item.created_at)));
}

export function requireCustomerActivity(value: unknown, customer: WorkCustomer, errorMessage: string): CustomerActivity {
  if (!isCustomerActivity(value) || value.customer_id !== customer.customer_id
    || (customer.provider && value.provider !== customer.provider)) {
    throw new Error(errorMessage);
  }
  return value;
}

export function requireCustomerActivities(value: unknown, customer: WorkCustomer, errorMessage: string): CustomerActivity[] {
  if (!Array.isArray(value)) throw new Error(errorMessage);
  return value.map(item => requireCustomerActivity(item, customer, errorMessage));
}

export function workError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error) && typeof error.response?.data?.detail === 'string') {
    return error.response.data.detail;
  }
  return error instanceof Error ? error.message : fallback;
}

export function israelInput(value: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(value);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

export function israelUtc(value: string, errorMessage: string): string {
  const naive = Date.parse(`${value}:00Z`);
  if (!Number.isFinite(naive)) throw new Error(errorMessage);
  const matches = [2, 3].map(offset => new Date(naive - offset * 3600000))
    .filter(date => israelInput(date) === value);
  if (matches.length !== 1) throw new Error(errorMessage);
  return matches[0].toISOString();
}

export function workDate(value: string | undefined, language: string): string {
  return value ? new Date(value).toLocaleString(language, { timeZone: 'Asia/Jerusalem' }) : '—';
}
